import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Search, Loader2, ExternalLink } from 'lucide-react';

const TYPE_LABEL = {
  resale: 'Resale',
  rent: 'Rent',
  resale_commercial: 'Resale Commercial',
  rent_commercial: 'Rent Commercial',
};

const AdminPropertiesPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [type, setType] = useState('all');
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    const params = { limit: 200 };
    if (q) params.q = q;
    if (type !== 'all') params.type = type;
    try {
      const { data } = await adminApi.get('/properties', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [q, type]);

  const remove = async (id, name) => {
    try {
      await adminApi.delete(`/properties/${id}`);
      toast({ title: 'Property deleted', description: name });
      setItems((prev) => prev.filter((p) => p.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <AdminLayout
      title="Properties"
      actions={<Link to="/admin/properties/new"><Button className="bg-[#9a1b2f] hover:bg-[#7a1524]"><Plus className="w-4 h-4 mr-1.5" />Add Property</Button></Link>}
    >
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setQ(searchInput); }} className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name, builder, city..." className="h-10 pl-9" />
          </div>
        </form>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-52 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Listing Types</SelectItem>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-sm text-gray-500">{loading ? '...' : `${total} total`}</div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">No properties found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Property</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">BHK</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                          <img src={p.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 line-clamp-1">{p.name}</div>
                          <div className="text-xs text-gray-500 line-clamp-1">{p.builder}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{TYPE_LABEL[p.listing_type] || p.listing_type}</Badge></td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{p.location}</td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{p.bhk}</td>
                    <td className="px-4 py-3 font-bold text-[#9a1b2f]">{p.price}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <a href={`/property/${p.id}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900" title="View"><ExternalLink className="w-3.5 h-3.5" /></a>
                        <Link to={`/admin/properties/${p.id}/edit`} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#9a1b2f]" title="Edit"><Pencil className="w-3.5 h-3.5" /></Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete property?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete <b>{p.name}</b>. This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(p.id, p.name)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPropertiesPage;
